import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getFirestore,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
} from "firebase/firestore";
import { JarvisTheme } from "../../theme/jarvisTheme";

type JarvisTaskDoc = {
  id: string;
  userId: string;
  title: string;
  completed: boolean;
  createdAt: Timestamp | null;
  completedAt: Timestamp | null;
};

export function TasksTab() {
  const [tasks, setTasks] = useState<JarvisTaskDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const uid = getAuth().currentUser?.uid ?? null;

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setError("Sign in required.");
      return;
    }

    const q = query(
      collection(getFirestore(), "jarvis_tasks"),
      where("userId", "==", uid),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: JarvisTaskDoc[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            userId: typeof data.userId === "string" ? data.userId : uid,
            title: typeof data.title === "string" ? data.title : "",
            completed: Boolean(data.completed),
            createdAt: data.createdAt ?? null,
            completedAt: data.completedAt ?? null,
          };
        });
        setTasks(rows);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message || "Failed to load tasks.");
        setLoading(false);
      },
    );

    return () => unsub();
  }, [uid]);

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      const aMs = a.createdAt?.toMillis?.() ?? 0;
      const bMs = b.createdAt?.toMillis?.() ?? 0;
      return bMs - aMs;
    });
  }, [tasks]);

  const addTask = useCallback(async () => {
    const title = newTitle.trim();
    if (!title || !uid) return;
    setSaving(true);
    setError(null);
    try {
      await addDoc(collection(getFirestore(), "jarvis_tasks"), {
        userId: uid,
        title,
        completed: false,
        createdAt: serverTimestamp(),
        completedAt: null,
      });
      setNewTitle("");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to add task.");
    } finally {
      setSaving(false);
    }
  }, [newTitle, uid]);

  const toggleTask = useCallback(async (task: JarvisTaskDoc) => {
    setError(null);
    const nextCompleted = !task.completed;
    try {
      await updateDoc(doc(getFirestore(), "jarvis_tasks", task.id), {
        completed: nextCompleted,
        completedAt: nextCompleted ? serverTimestamp() : null,
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update task.");
    }
  }, []);

  const removeTask = useCallback(async (taskId: string) => {
    setError(null);
    try {
      await deleteDoc(doc(getFirestore(), "jarvis_tasks", taskId));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to delete task.");
    }
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontFamily: JarvisTheme.fonts.ui,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontSize: "0.8rem",
            color: JarvisTheme.colors.muted,
            mb: 0.5,
            opacity: 0.85,
          }}
        >
          Tasks
        </Typography>
        <Typography
          sx={{
            opacity: 0.72,
            fontSize: "0.85rem",
            color: JarvisTheme.colors.muted,
          }}
        >
          Personal task list used as context for Jarvis chat.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          placeholder="New task…"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void addTask();
          }}
          disabled={!uid || saving}
          sx={{
            flex: 1,
            minWidth: 200,
            "& .MuiOutlinedInput-root": {
              fontFamily: JarvisTheme.fonts.ui,
              color: JarvisTheme.colors.text,
              bgcolor: JarvisTheme.colors.panelSoft,
              "& fieldset": { borderColor: JarvisTheme.colors.panelBorder },
              "&:hover fieldset": { borderColor: JarvisTheme.colors.accentBorder },
              "&.Mui-focused fieldset": {
                borderColor: JarvisTheme.colors.accent,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: JarvisTheme.colors.muted,
              opacity: 0.7,
            },
          }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => void addTask()}
          disabled={!uid || saving || !newTitle.trim()}
          sx={{
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            borderColor: JarvisTheme.colors.accentBorder,
            color: JarvisTheme.colors.muted,
            "&:hover": { borderColor: JarvisTheme.colors.accent },
          }}
        >
          Add
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress
            size={22}
            sx={{ color: JarvisTheme.colors.accent }}
          />
        </Box>
      ) : sortedTasks.length === 0 ? (
        <Alert severity="info">No tasks yet. Add one above.</Alert>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sortedTasks.map((task) => (
            <Paper
              key={task.id}
              elevation={0}
              sx={{
                p: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRadius: 1,
                border: `1px solid ${JarvisTheme.colors.panelBorder}`,
                bgcolor: JarvisTheme.colors.panelSoft,
              }}
            >
              <Checkbox
                checked={task.completed}
                onChange={() => void toggleTask(task)}
                sx={{
                  color: JarvisTheme.colors.muted,
                  "&.Mui-checked": { color: JarvisTheme.colors.accent },
                }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontFamily: JarvisTheme.fonts.ui,
                  color: JarvisTheme.colors.text,
                  textDecoration: task.completed ? "line-through" : "none",
                  opacity: task.completed ? 0.55 : 1,
                }}
              >
                {task.title}
              </Typography>
              <IconButton
                size="small"
                aria-label="Delete task"
                onClick={() => void removeTask(task.id)}
                sx={{ color: JarvisTheme.colors.muted }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
